import Dropdown from '@/componentsDS/Dropdown/Dropdown';
import Icon from '@/componentsDS/Icon/Icon';
import Input from '@/componentsDS/Input/Input';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

const InputDropdown = ({ id, name, label, labelInfoText, variant, size, fullWidth = false, required, disabled, infoText, errorMessage, captionText, value, onChange, dropdownOption, dropdownSelectedOption, onSelectDropdownItem, multipleSelection = false, allowSelectAll, isLoading, placeholder="Search" }) => {
	const [dropdownWidth, setDropdownWidth] = useState();
	const [filteredOptions, setFilteredOptions] = useState(dropdownOption);
	const [isClearing, setIsClearing] = useState(false);
	const [chipValues, setChipValues] = useState([]);
	const [isOpen, setIsOpen] = useState(false);
	const [noResults, setNoResults] = useState(false);
	const inputRef = useRef(null);

	useEffect(() =>{
		if(dropdownOption.length> 0){

			setNoResults(false)
		}
	}, [dropdownOption])
	// Measure the width of the Input component to set the dropdown width dynamically with the input width
	useEffect(() => {
		if (inputRef.current) {
			const inputElement = inputRef.current.querySelector('.DS__input')
			if (inputElement) {
				setDropdownWidth(inputElement.offsetWidth); // Measure the `.DS__input` width
			}
		}
	}, [fullWidth, isOpen]);

	useEffect(() => {
		if (!isOpen && multipleSelection) {
			handleInputChange('');
		}
	}, [isOpen]);

	useEffect(() => {
		if (multipleSelection) {
			const selectedValue = dropdownSelectedOption.map((item) => ({
				...item,
				id: String(item.id),
				label: item.label
			}));
			setChipValues(selectedValue);
		}
	}, [dropdownSelectedOption, multipleSelection]);

	const handleInputChange = (newVal) => {
		onChange(newVal);

		if (newVal?.target) { newVal = newVal.target.value }

		const filtered = dropdownOption.filter((option) => option.label.toLowerCase().includes(newVal.toLowerCase()));

		if (filtered.length === 0) {
			setNoResults(true);
		} else {
			setNoResults(false);
		}

		setFilteredOptions(filtered);

		if (newVal === '') {
			setIsClearing(true);
		}

		if (newVal === '' && !multipleSelection) {
			onSelectDropdownItem([]);
		}
	};

	const handleDropdownSelect = (selectedItems) => {
		if (isClearing) {
			setIsClearing(false); // Stop clearing once selection is changed
			if (!isOpen) return; // Prevent infinite re-rendering when dropdown is closed
		}

		onSelectDropdownItem(selectedItems);

		if (!multipleSelection) {
			setFilteredOptions(dropdownOption);
			const selectedValue = selectedItems[0]?.label || '';
			onChange(selectedValue);
		} else {
			const selectedValue = selectedItems.map((item) => ({
				...item,
				id: String(item.id),
				label: item.label
			}));
			setChipValues(selectedValue);
		}
	};

	const handleClearChipSelection = () => {
		setIsClearing(true);
		setChipValues([]);
		onSelectDropdownItem([]);
		setFilteredOptions(dropdownOption);
		handleInputChange('');
		setIsOpen(false);
	};

	const onInputClick = () => {
		setIsOpen(true);
	};

	const suffixIcon = <Icon name="chevron-down" style="solid" size="small" />;

	const DropdownTrigger = (
		<div ref={inputRef}>
			<Input
				id={id}
				name={name}
				placeholder={placeholder}
				suffixIcon={suffixIcon}
				label={label}
				labelInfoText={labelInfoText}
				variant={variant}
				size={size}
				required={required}
				disabled={disabled}
				infoText={infoText}
				errorMessage={errorMessage}
				captionText={captionText}
				value={value}
				onChange={handleInputChange}
				onClick={onInputClick}
				chipInputMultiple={multipleSelection}
				chipValues={multipleSelection ? chipValues : undefined}
				setChipValues={setChipValues}
				onClearChips={handleClearChipSelection}
				fullWidth={fullWidth}
			/>
		</div>
	);

	return (
		<div style={{ width: fullWidth ? '100%' : '350px' }}>
			<Dropdown
				options={dropdownOption}
				filteredOptions={filteredOptions}
				value={dropdownSelectedOption}
				onAdd={handleDropdownSelect}
				trigger={DropdownTrigger}
				multiple={multipleSelection}
				allowSelectAll={allowSelectAll}
				isOpen={isOpen}
				setIsOpen={setIsOpen}
				size={size}
				disabled={disabled}
				setWidth={dropdownWidth}
				isLoading={isLoading}
				noResults={noResults}
				fullWidth={fullWidth}
			/>
		</div>
	);
};

export default InputDropdown;

InputDropdown.propTypes = {
	id: PropTypes.string,
	name: PropTypes.string,
	label: PropTypes.string,
	labelInfoText: PropTypes.string,
	variant: PropTypes.oneOf(['outlined', 'filled', 'underlined']),
	size: PropTypes.oneOf(['default', 'small']),
	fullWidth: PropTypes.bool,
	required: PropTypes.bool,
	disabled: PropTypes.bool,
	infoText: PropTypes.string,
	errorMessage: PropTypes.string,
	captionText: PropTypes.string,
	value: PropTypes.string,
	onChange: PropTypes.func,
	dropdownOption: PropTypes.array,
	dropdownSelectedOption: PropTypes.array,
	onSelectDropdownItem: PropTypes.func,
	multipleSelection: PropTypes.bool,
	allowSelectAll: PropTypes.bool,
	isLoading: PropTypes.bool,
	placeholder: PropTypes.string
};
